import { AxiosInstance } from 'axios';
import { result, Result } from 'util/result';
import Terra from '../Terra';
import {
  ServiceIntegrationResponse,
  ServiceIntegrationsResponse,
} from 'data/terra/api/terra/response/ServiceIntegrationResponse';
import { CreateServiceIntegrationRequest } from 'data/terra/ServiceIntegration';
import {
  BaseConfig,
  CreateServiceIntegrationConfigRequest,
  ServiceConfigIntegrationDescription,
} from 'data/terra/ServiceConfig';
import {
  InitServiceIntegrationResponse,
  ServiceIntegrationConfigResponse,
  ServiceIntegrationConfigsResponse,
} from 'data/terra/api/terra/response/ServiceIntegrationConfigResponse';
import {
  InitServiceIntegrationRequest,
  SyncServiceIntegrationConfigRequest,
} from 'data/terra/ServiceIntegrationConfig';
import { apiResult } from 'util/apiResult';

class ServiceIntegrationEndpoint {
  getServiceIntegrationsUrl = () =>
    `${Terra.TERRA_HOST}/${Terra.TERRA_BASE_API_URL}/service-integrations`;
  getServiceIntegrationDetailUrl = (integrationId: string) =>
    `${Terra.TERRA_HOST}/${Terra.TERRA_BASE_API_URL}/service-integrations/${integrationId}`;
  getServiceIntegrationConfigsUrl = (integrationId: string) =>
    `${Terra.TERRA_HOST}/${Terra.TERRA_BASE_API_URL}/service-integrations/${integrationId}/configs`;
  getServiceIntegrationConfigDetailUrl = (
    integrationId: string,
    configId: string
  ) =>
    `${Terra.TERRA_HOST}/${Terra.TERRA_BASE_API_URL}/service-integrations/${integrationId}/configs/${configId}`;
  getInitServiceIntegrationFromScratchUrl = () =>
    `${Terra.TERRA_HOST}/${Terra.TERRA_BASE_API_URL}/service-integrations/init`;
  getSyncServiceIntegrationConfigUrl = (
    integrationId: string,
    configId: string
  ) =>
    `${Terra.TERRA_HOST}/${Terra.TERRA_BASE_API_URL}/service-integrations/${integrationId}/configs/${configId}/sync`;
}

export class ServiceIntegrationApi {
  terraClient: AxiosInstance;
  endpoint = new ServiceIntegrationEndpoint();

  constructor(client: AxiosInstance) {
    this.terraClient = client;
  }

  createServiceIntegration(
    request: CreateServiceIntegrationRequest
  ): Promise<Result<ServiceIntegrationResponse>> {
    return result(
      async () =>
        (
          await this.terraClient.post<ServiceIntegrationResponse>(
            this.endpoint.getServiceIntegrationsUrl(),
            request
          )
        ).data
    );
  }

  fetchServiceIntegrations(
    appId: string,
    appVersionId: string
  ): Promise<Result<ServiceIntegrationsResponse>> {
    return result(
      async () =>
        (
          await this.terraClient.get<ServiceIntegrationsResponse>(
            this.endpoint.getServiceIntegrationsUrl(),
            {
              params: {
                appId,
                appVersionId,
                pageSize: 200,
              },
            }
          )
        ).data
    );
  }

  deleteServiceIntegration(integrationId: string): Promise<Result<void>> {
    return result(
      async () =>
        (
          await this.terraClient.delete<void>(
            this.endpoint.getServiceIntegrationDetailUrl(integrationId)
          )
        ).data
    );
  }

  createServiceIntegrationConfig(
    integrationId: string,
    request: CreateServiceIntegrationConfigRequest
  ): Promise<Result<ServiceIntegrationConfigResponse>> {
    return result(
      async () =>
        (
          await this.terraClient.post<ServiceIntegrationConfigResponse>(
            this.endpoint.getServiceIntegrationConfigsUrl(integrationId),
            request
          )
        ).data
    );
  }

  fetchServiceIntegrationConfigs(
    integrationId: string
  ): Promise<Result<ServiceIntegrationConfigsResponse>> {
    return result(
      async () =>
        (
          await this.terraClient.get<ServiceIntegrationConfigsResponse>(
            this.endpoint.getServiceIntegrationConfigsUrl(integrationId)
          )
        ).data
    );
  }

  updateServiceIntegrationConfig(
    integrationId: string,
    integrationConfigId: string,
    data: ServiceConfigIntegrationDescription<BaseConfig>
  ): Promise<Result<ServiceIntegrationConfigResponse>> {
    return result(
      async () =>
        (
          await this.terraClient.put<ServiceIntegrationConfigResponse>(
            this.endpoint.getServiceIntegrationConfigDetailUrl(
              integrationId,
              integrationConfigId
            ),
            data
          )
        ).data
    );
  }

  deleteServiceIntegrationConfig(
    integrationId: string,
    integrationConfigId: string
  ): Promise<Result<void>> {
    return result(
      async () =>
        (
          await this.terraClient.delete<void>(
            this.endpoint.getServiceIntegrationConfigDetailUrl(
              integrationId,
              integrationConfigId
            )
          )
        ).data
    );
  }

  initServiceIntegrationConfig(
    request: InitServiceIntegrationRequest
  ): Promise<Result<InitServiceIntegrationResponse>> {
    return apiResult(
      async () =>
        (
          await this.terraClient.post<InitServiceIntegrationResponse>(
            this.endpoint.getInitServiceIntegrationFromScratchUrl(),
            request
          )
        ).data
    );
  }

  syncServiceIntegrationConfig(
    integrationId: string,
    integrationConfigId: string,
    request: SyncServiceIntegrationConfigRequest
  ): Promise<Result<void>> {
    return apiResult(
      async () =>
        (
          await this.terraClient.post<void>(
            this.endpoint.getSyncServiceIntegrationConfigUrl(
              integrationId,
              integrationConfigId
            ),
            request
          )
        ).data
    );
  }
}
